import Button from '@rio-cloud/rio-uikit/Button';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import NotFoundState from '@rio-cloud/rio-uikit/NotFoundState';
import NoData from '@rio-cloud/rio-uikit/NoData';
import Table, {
    TableBatchDropdown,
    TableBody,
    TableColumn,
    TableHeader,
    TableHeaderColumn,
    TableRow,
    type TableRowId,
    type TableSortDirection,
} from '@rio-cloud/rio-uikit/Table';
import UikitTag from '@rio-cloud/rio-uikit/Tag';
import UikitTagList from '@rio-cloud/rio-uikit/TagList';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';
import { ERROR_CODE_GENERIC, ERROR_CODE_NAME_ALREADY_IN_USE } from '../details/TagListDetails';
import { closableSuccessNotification, nonVanishingErrorNotification } from '../../ClickableNotifications';
import {
    getAssignedAssets,
    getAssignedAssetsLoaded,
    getAssignedDrivers,
    getAssignedDriversLoaded,
    getSelectedTag,
    getTags,
    getTagsDeletionInProgress,
    getTagsDeletionLastFailureTagName,
    getTagsDeletionLastSuccessTagName,
    getTagUpdateLastErrorCode,
    getTagUpdateSuccessful,
    getUnsavedChanges,
} from '../Tags.selector';
import { getSearchString, getTableViewType } from './toolbar/tagListToolbarSlice';
import { useAppDispatch, useAppSelector } from '../../../../configuration/setup/hooks';
import { deleteTags } from '../tagsThunks';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import AccentBar from '@rio-cloud/rio-uikit/AccentBar';
import type { Tag } from '../types';
import { openTagCreationDialog } from '../create/tagCreationDialogSlice';
import {
    defaultTagTableColumns,
    TAG_TABLE_COLUMNS,
    type TagListSortKey,
    type TagTableColumnConfig,
} from './tagListUtils';

type TagTableColumnKey = TagListSortKey;
const SORT_QUERY_PARAM = 'sort';

const MAX_VISIBLE_ASSIGNED_ITEMS = 2;
const SEARCH_HIGHLIGHT_CLASS = 'search-result-highlight';
const SEARCH_IGNORE_SELECTOR = '[data-search-ignore="true"]';

const isSearchHighlightSupported = () =>
    typeof window !== 'undefined' && 'CSS' in window && 'highlights' in CSS && typeof Highlight !== 'undefined';

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const parseSortQuery = (search: string): { sortBy: TagTableColumnKey; sortDirection: TableSortDirection } => {
    const rawSort = search.match(/(?:^|&)sort=([^&]*)/)?.[1] ?? null;

    if (!rawSort || rawSort.length < 2) {
        return {
            sortBy: 'name',
            sortDirection: 'asc',
        };
    }

    const decodedSort = rawSort.replace(/%2B/gi, '+').replace(/%2D/gi, '-');
    const sortDirection = decodedSort.startsWith('-') ? 'desc' : 'asc';
    const sortBy = decodedSort.substring(1) as TagTableColumnKey;

    if (!TAG_TABLE_COLUMNS.some(column => column.key === sortBy)) {
        return {
            sortBy: 'name',
            sortDirection: 'asc',
        };
    }

    return {
        sortBy,
        sortDirection,
    };
};

const createSortQuery = (sortBy: TagTableColumnKey, sortDirection: TableSortDirection) =>
    `${sortDirection === 'asc' ? '+' : '-'}${sortBy}`;

const collectSearchHighlightRanges = (root: HTMLElement, query: string): Range[] => {
    const ranges: Range[] = [];

    if (!query) {
        return ranges;
    }

    const regex = new RegExp(escapeRegExp(query), 'gi');
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: node => {
            const parentElement = node.parentElement;

            if (!parentElement || parentElement.closest(SEARCH_IGNORE_SELECTOR)) {
                return NodeFilter.FILTER_REJECT;
            }

            return NodeFilter.FILTER_ACCEPT;
        },
    });

    let node: Node | null = walker.nextNode();

    while (node) {
        const text = node.textContent ?? '';
        regex.lastIndex = 0;

        let match: RegExpExecArray | null = regex.exec(text);

        while (match) {
            const range = document.createRange();
            range.setStart(node, match.index);
            range.setEnd(node, match.index + match[0].length);
            ranges.push(range);

            match = regex.exec(text);
        }

        node = walker.nextNode();
    }

    return ranges;
};

const renderNoData = () => <NoData text={<FormattedMessage id='intl-msg:common-message.noData' />} />;

export const sortTagsByColumn = (
    tagItems: Tag[],
    sortKey: TagListSortKey,
    sortDirection: TableSortDirection,
    assetsByTagId: Record<string, unknown[] | undefined>,
    assetsLoaded: boolean,
    driversByTagId: Record<string, unknown[] | undefined>,
    driversLoaded: boolean
) => {
    const compareByName = (leftName: string, rightName: string) =>
        leftName.localeCompare(rightName, undefined, { sensitivity: 'base' });
    const compareWithDirection = (comparison: number) => (sortDirection === 'asc' ? comparison : -comparison);

    const getAssignedAssetsCount = (tagId: string) => (assetsLoaded ? (assetsByTagId[tagId]?.length ?? 0) : 0);
    const getAssignedDriversCount = (tagId: string) => (driversLoaded ? (driversByTagId[tagId]?.length ?? 0) : 0);

    return [...tagItems].sort((leftTag, rightTag) => {
        switch (sortKey) {
            case 'assignedAssets': {
                const countDifference = getAssignedAssetsCount(leftTag.id) - getAssignedAssetsCount(rightTag.id);
                if (countDifference !== 0) {
                    return compareWithDirection(countDifference);
                }

                return compareWithDirection(compareByName(leftTag.name, rightTag.name));
            }
            case 'assignedDrivers': {
                const countDifference = getAssignedDriversCount(leftTag.id) - getAssignedDriversCount(rightTag.id);
                if (countDifference !== 0) {
                    return compareWithDirection(countDifference);
                }

                return compareWithDirection(compareByName(leftTag.name, rightTag.name));
            }
            default: {
                const nameDifference = compareByName(leftTag.name, rightTag.name);
                return sortDirection === 'asc' ? nameDifference : -nameDifference;
            }
        }
    });
};

export interface TagListProps {
    toggleDiscardChangesDialog: (opened: boolean, rowId: string | null) => void;
    columns: TagTableColumnConfig[];
    compactRows: boolean;
    gridLines: boolean;
    tableResetKey: number;
    onColumnOrderChange: (columnOrder: string[]) => void;
    onColumnResize: (columnKey: string, width?: number | string) => void;
}

export const TagList = (props: TagListProps) => {
    const {
        toggleDiscardChangesDialog,
        columns,
        compactRows,
        gridLines,
        tableResetKey,
        onColumnOrderChange,
        onColumnResize,
    } = props;

    const tags = useAppSelector(getTags);
    const tableViewType = useAppSelector(getTableViewType);
    const searchString = useAppSelector(getSearchString);
    const assignedAssets = useAppSelector(getAssignedAssets);
    const assignedAssetsLoaded = useAppSelector(getAssignedAssetsLoaded);
    const assignedDrivers = useAppSelector(getAssignedDrivers);
    const assignedDriversLoaded = useAppSelector(getAssignedDriversLoaded);
    const tagsDeletionInProgress = useAppSelector(getTagsDeletionInProgress);
    const selectedTag = useAppSelector(getSelectedTag);
    const unsavedTagChanges = useAppSelector(getUnsavedChanges);
    const deleteTagLastSuccessTagName = useAppSelector(getTagsDeletionLastSuccessTagName);
    const deleteTagLastFailureTagName = useAppSelector(getTagsDeletionLastFailureTagName);
    const updateTagSuccessful = useAppSelector(getTagUpdateSuccessful);
    const updateTagLastErrorCode = useAppSelector(getTagUpdateLastErrorCode);
    const intl = useIntl();

    const dispatch = useAppDispatch();
    const handleCreateTag = () => dispatch(openTagCreationDialog());
    const tableContainerRef = useRef<HTMLDivElement>(null);

    const location = useLocation();
    const navigate = useNavigate();
    const initialSort = parseSortQuery(location.search);
    const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
    const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
    const [sortBy, setSortBy] = useState<TagTableColumnKey>(initialSort.sortBy);
    const [sortDirection, setSortDirection] = useState<TableSortDirection>(initialSort.sortDirection);

    useEffect(() => {
        if (!tagsDeletionInProgress) {
            setShowDeleteDialog(false);
        }
    }, [tagsDeletionInProgress]);

    useEffect(() => {
        if (deleteTagLastSuccessTagName) {
            closableSuccessNotification(
                <FormattedMessage id='tags.tagList.delete.success' values={{ tagName: deleteTagLastSuccessTagName }} />,
                'success-notification-tag-deleted'
            );
        }
        if (deleteTagLastFailureTagName) {
            nonVanishingErrorNotification(
                <FormattedMessage id='tags.tagList.delete.failure' values={{ tagName: deleteTagLastFailureTagName }} />,
                'T01'
            );
        }
    }, [deleteTagLastSuccessTagName, deleteTagLastFailureTagName]);

    useEffect(() => {
        if (updateTagSuccessful) {
            closableSuccessNotification(
                <FormattedMessage id='tags.tagDetails.tagUpdate.success' />,
                'success-notification-tag-updated'
            );
        }
        if (updateTagLastErrorCode) {
            const messageId =
                updateTagLastErrorCode === ERROR_CODE_NAME_ALREADY_IN_USE
                    ? 'tags.tagDetails.tagUpdate.failure.invalidTagName'
                    : 'tags.tagDetails.tagUpdate.failure';
            nonVanishingErrorNotification(
                <FormattedMessage id={messageId} />,
                updateTagLastErrorCode ?? ERROR_CODE_GENERIC
            );
        }
    }, [updateTagSuccessful, updateTagLastErrorCode]);

    const renderAssignedTagList = <T extends { name?: string; displayName?: string }>(
        items: T[] | undefined,
        loaded: boolean,
        getLabel: (item: T) => string
    ): ReactNode => {
        if (!loaded) {
            return '...';
        }

        const assignedItems = items ?? [];
        const fullSummary = assignedItems.map(getLabel).filter(Boolean).join(', ');
        const visibleItems = assignedItems.slice(0, MAX_VISIBLE_ASSIGNED_ITEMS).map(getLabel).filter(Boolean);
        const hiddenItemCount = assignedItems.length - visibleItems.length;

        if (visibleItems.length === 0 && hiddenItemCount === 0) {
            return renderNoData();
        }

        return (
            <div title={fullSummary || undefined}>
                <UikitTagList className='display-flex flex-wrap align-items-center'>
                    {visibleItems.map(label => (
                        <UikitTag key={label} size='small'>
                            {label}
                        </UikitTag>
                    ))}
                    {hiddenItemCount > 0 && (
                        <div className='margin-bottom-5'>
                            <span className='badge badge-muted'>+{hiddenItemCount}</span>
                        </div>
                    )}
                </UikitTagList>
            </div>
        );
    };

    const filterTagsBySearchValue = (tagItems: Tag[]): Tag[] => {
        const searchTerms = searchString.toLocaleLowerCase().split(' ').filter(Boolean);

        if (searchTerms.length === 0) {
            return tagItems;
        }

        return searchTerms.reduce(
            (filteredTags, searchTerm) => filteredTags.filter(tag => tag.name.toLocaleLowerCase().includes(searchTerm)),
            tagItems
        );
    };

    const filteredTags = filterTagsBySearchValue(tags);
    const sortedTags = sortTagsByColumn(
        filteredTags,
        sortBy,
        sortDirection,
        assignedAssets,
        assignedAssetsLoaded,
        assignedDrivers,
        assignedDriversLoaded
    );

    useEffect(() => {
        if (!isSearchHighlightSupported()) {
            return;
        }

        const tableBody = tableContainerRef.current?.querySelector('.table-body') as HTMLElement | null;

        if (!tableBody) {
            return;
        }

        const highlight = CSS.highlights.get(SEARCH_HIGHLIGHT_CLASS) ?? new Highlight();
        CSS.highlights.set(SEARCH_HIGHLIGHT_CLASS, highlight);

        const query = searchString.trim();
        const raf = window.requestAnimationFrame(() => {
            highlight.clear();

            for (const range of collectSearchHighlightRanges(tableBody, query)) {
                highlight.add(range);
            }
        });

        return () => {
            window.cancelAnimationFrame(raf);
            highlight.clear();
        };
    }, [searchString, sortedTags, columns]);

    const renderColumnValue = (column: TagTableColumnKey, tag: Tag): ReactNode => {
        switch (column) {
            case 'name':
                return tag.name;
            case 'assignedAssets':
                return renderAssignedTagList(assignedAssets[tag.id], assignedAssetsLoaded, item => item.name);
            case 'assignedDrivers':
                return renderAssignedTagList(assignedDrivers[tag.id], assignedDriversLoaded, item => item.displayName);
            default:
                throw Error('column from ColumnConfig not handled in renderColumnValue - please add it');
        }
    };

    const handleToggleAll = (shouldSelect: boolean) => {
        const allRowIds = tags.map(tag => tag.id);

        setSelectedTagIds(shouldSelect ? allRowIds : []);
    };

    const handleSelectionChange = (rowIds: TableRowId[]) => {
        setSelectedTagIds(rowIds.map(rowId => String(rowId)));
    };

    const handleSortChange = (columnKey: string, direction: TableSortDirection) => {
        const nextSortBy = columnKey as TagTableColumnKey;

        setSortBy(nextSortBy);
        setSortDirection(direction);

        const searchParams = new URLSearchParams(location.search);
        searchParams.set(SORT_QUERY_PARAM, createSortQuery(nextSortBy, direction));
        const nextSearch = searchParams
            .toString()
            .replace(/sort=%2B/gi, 'sort=+')
            .replace(/sort=%2D/gi, 'sort=-');

        navigate(
            {
                pathname: location.pathname,
                search: nextSearch ? `?${nextSearch}` : '',
            },
            { replace: true }
        );
    };

    const handleActiveRowChange = (rowId: TableRowId | undefined) => {
        if (rowId == null) {
            return;
        }

        const nextRowId = String(rowId);

        if (unsavedTagChanges) {
            toggleDiscardChangesDialog(true, nextRowId);
            return;
        }

        navigate({
            pathname: `/tags/${nextRowId}`,
            search: location.search,
        });
    };

    const abortDelete = () => setShowDeleteDialog(false);

    const confirmDelete = () => {
        dispatch(deleteTags({ tagIds: selectedTagIds }));
        setSelectedTagIds([]);
    };

    const batchDropdownItems = [
        {
            value: (
                <div data-cy='tags-table-multiselect-delete-button'>
                    <span className='rioglyph rioglyph-trash margin-right-10 text-size-18' />
                    <FormattedMessage id='tags.tagList.deleteSelected' />
                </div>
            ),
            disabled: selectedTagIds.length === 0,
            onSelect: () => setShowDeleteDialog(true),
        },
        {
            divider: true,
        },
        {
            value: (
                <div>
                    <FormattedMessage id='tags.tagList.selectAll' />
                </div>
            ),
            onSelect: () => handleToggleAll(true),
        },
        {
            value: (
                <div>
                    <FormattedMessage id='tags.tagList.deselectAll' />
                </div>
            ),
            onSelect: () => handleToggleAll(false),
        },
    ];
    const visibleColumns = columns.filter(column => column.visible);

    return (
        <>
            <div id='TagsTable' ref={tableContainerRef}>
                {tags.length ? (
                    filteredTags.length ? (
                        <Table
                            key={`tags-table-${tableResetKey}`}
                            rowKey='id'
                            viewType={tableViewType}
                            activeRowId={selectedTag?.id ?? undefined}
                            condensed={compactRows}
                            gridLines={gridLines}
                            hover
                            onActiveRowChange={handleActiveRowChange}
                            onColumnOrderChange={onColumnOrderChange}
                            onColumnResize={onColumnResize}
                            onSortChange={handleSortChange}
                            onSelectionChange={handleSelectionChange}
                            sortBy={sortBy}
                            sortDirection={sortDirection}
                            columnOrder={columns.map(column => column.key)}
                            selectedRowIds={selectedTagIds}
                            showSelectionColumn
                            selectionHeaderContent={
                                <div data-cy='tags-table-multiselect-button'>
                                    <TableBatchDropdown items={batchDropdownItems} />
                                </div>
                            }
                            stickyReference='applicationLayout'
                            responsiveBreakpoint={{ list: '800px' }}
                        >
                            <TableHeader sticky>
                                {visibleColumns.map(column => (
                                    <TableHeaderColumn
                                        key={column.key}
                                        columnKey={column.key}
                                        draggable={column.draggable}
                                        resizeable={column.resizeable}
                                        sortable={column.sortable}
                                        width={column.width}
                                    >
                                        <FormattedMessage id={column.label} />
                                    </TableHeaderColumn>
                                ))}
                            </TableHeader>
                            <TableBody aria-label='fleet group table content'>
                                {sortedTags.map(row => (
                                    <TableRow
                                        key={row.id}
                                        id={row.id}
                                        data-cy='tags-table-row'
                                        data-testid='tags-table-row'
                                    >
                                        {visibleColumns.map(column => (
                                            <TableColumn key={column.key} columnKey={column.key} overflow='ellipsis'>
                                                <span>{renderColumnValue(column.key, row)}</span>
                                            </TableColumn>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <div className='display-flex justify-content-center margin-top-15pct'>
                            <NotFoundState
                                headline={
                                    <FormattedMessage
                                        id='tags.tagList.search.empty'
                                        defaultMessage='No tags match your search'
                                    />
                                }
                                message=''
                                buttons={[
                                    {
                                        autoTrackingKey: 'tags.tagListToolbar.addButton',
                                        onClick: handleCreateTag,
                                        text: <FormattedMessage id='tags.tagListToolbar.addButton' />,
                                    },
                                ]}
                            />
                        </div>
                    )
                ) : (
                    <div className='display-flex justify-content-center margin-top-15pct'>
                        <FormattedMessage id='tags.noTagsFound'>
                            {notFoundMsg => (
                                <NotFoundState
                                    headline={notFoundMsg}
                                    message=''
                                    buttons={[
                                        {
                                            autoTrackingKey: 'tags.tagListToolbar.addButton',
                                            onClick: handleCreateTag,
                                            text: <FormattedMessage id='tags.tagListToolbar.addButton' />,
                                        },
                                    ]}
                                />
                            )}
                        </FormattedMessage>
                    </div>
                )}
            </div>
            <div data-cy='tags-delete-confirmation-dialog'>
                <Dialog
                    show={showDeleteDialog}
                    title={
                        <FormattedMessage
                            id={
                                selectedTagIds.length === 1
                                    ? 'tags.tagDetails.deleteConfirmationDialog.title'
                                    : 'tags.tagList.deleteConfirmationDialog.title.multiple'
                            }
                        />
                    }
                    bsSize='sm'
                    body={
                        <>
                            <div>
                                <FormattedMessage
                                    id={
                                        selectedTagIds.length === 1
                                            ? 'tags.tagDetails.deleteConfirmationDialog.content.text'
                                            : 'tags.tagList.deleteConfirmationDialog.content.text.multiple'
                                    }
                                    values={{ tagName: tags.find(it => it.id === selectedTagIds[0])?.name ?? '' }}
                                />
                            </div>
                            <div className='margin-top-10'>
                                <AccentBar color='bg-warning'>
                                    <div>
                                        <div className='text-medium margin-bottom-5'>
                                            <FormattedMessage id='tags.tagDetails.deleteConfirmationDialog.content.accent.text' />
                                        </div>
                                        <ul className='margin-bottom-0'>
                                            <li>
                                                <FormattedMessage
                                                    id={
                                                        selectedTagIds.length === 1
                                                            ? 'tags.tagDetails.deleteConfirmationDialog.content.accent.bullet1'
                                                            : 'tags.tagList.deleteConfirmationDialog.content.accent.bullet1.multiple'
                                                    }
                                                />
                                            </li>
                                            <li>
                                                <FormattedMessage
                                                    id={
                                                        selectedTagIds.length === 1
                                                            ? 'tags.tagDetails.deleteConfirmationDialog.content.accent.bullet2'
                                                            : 'tags.tagList.deleteConfirmationDialog.content.accent.bullet2.multiple'
                                                    }
                                                />
                                            </li>
                                        </ul>
                                    </div>
                                </AccentBar>
                            </div>
                        </>
                    }
                    footer={
                        <div className='btn-toolbar'>
                            <Button
                                autoTrackingKey='tags.tagList.deleteConfirmationDialog.button.cancel'
                                onClick={abortDelete}
                            >
                                <FormattedMessage id='tags.tagList.deleteConfirmationDialog.button.cancel' />
                            </Button>
                            <Button
                                autoTrackingKey={
                                    selectedTagIds.length === 1
                                        ? 'tags.tagDetails.deleteConfirmationDialog.button.confirm'
                                        : 'tags.tagList.deleteConfirmationDialog.button.confirm.multiple'
                                }
                                bsStyle={Button.WARNING}
                                onClick={confirmDelete}
                            >
                                <FormattedMessage
                                    id={
                                        selectedTagIds.length === 1
                                            ? 'tags.tagDetails.deleteConfirmationDialog.button.confirm'
                                            : 'tags.tagList.deleteConfirmationDialog.button.confirm.multiple'
                                    }
                                />
                            </Button>
                        </div>
                    }
                    onEsc={abortDelete}
                    useOverflow
                />
            </div>
        </>
    );
};

export { defaultTagTableColumns };
export type { TagListSortKey, TagTableColumnConfig };
